'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface VolunteerApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunityTitle: string;
  opportunityId: number;
  onSubmit: (data: VolunteerApplicationData) => Promise<void>;
}

export interface VolunteerApplicationData {
  opportunityTitle: string;
  volunteerName: string;
  volunteerEmail: string;
  volunteerPhone: string;
  skills: string[];
  availability: string;
  message: string;
}

const VolunteerApplicationModal: React.FC<VolunteerApplicationModalProps> = ({
  isOpen,
  onClose,
  opportunityTitle,
  onSubmit
}) => {
  const [formData, setFormData] = useState<VolunteerApplicationData>({
    opportunityTitle,
    volunteerName: '',
    volunteerEmail: '',
    volunteerPhone: '',
    skills: [],
    availability: '',
    message: ''
  });

  const [skillInput, setSkillInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.volunteerName.trim()) {
      newErrors.volunteerName = 'Full name is required';
    }

    if (!formData.volunteerEmail.trim()) {
      newErrors.volunteerEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.volunteerEmail)) {
      newErrors.volunteerEmail = 'Please enter a valid email address';
    }

    if (!formData.volunteerPhone.trim()) {
      newErrors.volunteerPhone = 'Phone number is required';
    }

    if (formData.skills.length === 0) {
      newErrors.skills = 'Please add at least one skill';
    }

    if (!formData.availability.trim()) {
      newErrors.availability = 'Availability is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Please tell us why you want to volunteer';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      setSubmitSuccess(true);
      setTimeout(() => {
        onClose();
        resetForm();
      }, 2000);
    } catch (error) {
      setErrors({ submit: 'Failed to submit application. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      opportunityTitle,
      volunteerName: '',
      volunteerEmail: '',
      volunteerPhone: '',
      skills: [],
      availability: '',
      message: ''
    });
    setSkillInput('');
    setErrors({});
    setSubmitSuccess(false);
  };

  const handleAddSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData({
        ...formData,
        skills: [...formData.skills, skillInput.trim()]
      });
      setSkillInput('');
      setErrors({ ...errors, skills: '' });
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter(skill => skill !== skillToRemove)
    });
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      resetForm();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-border">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-headline font-bold text-foreground">
              Volunteer Application
            </h2>
            <p className="text-sm text-muted-foreground font-body mt-1">
              {opportunityTitle}
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          >
            <Icon name="XMarkIcon" size={24} variant="outline" />
          </button>
        </div>

        {/* Success Message */}
        {submitSuccess && (
          <div className="mx-6 mt-6 p-4 bg-success/10 border border-success rounded-lg flex items-center space-x-3">
            <Icon name="CheckCircleIcon" size={24} className="text-success" variant="solid" />
            <div>
              <p className="font-cta font-bold text-success">Application Submitted!</p>
              <p className="text-sm text-success/80 font-body">We'll review your application and get back to you soon.</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-cta font-bold text-foreground mb-2">
              Full Name *
            </label>
            <input
              type="text"
              value={formData.volunteerName}
              onChange={(e) => setFormData({ ...formData, volunteerName: e.target.value })}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground font-body"
              placeholder="Enter your full name"
              disabled={isSubmitting}
            />
            {errors.volunteerName && (
              <p className="text-error text-sm mt-1 font-body">{errors.volunteerName}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-cta font-bold text-foreground mb-2">
              Email Address *
            </label>
            <input
              type="email"
              value={formData.volunteerEmail}
              onChange={(e) => setFormData({ ...formData, volunteerEmail: e.target.value })}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground font-body"
              placeholder="your.email@example.com"
              disabled={isSubmitting}
            />
            {errors.volunteerEmail && (
              <p className="text-error text-sm mt-1 font-body">{errors.volunteerEmail}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-cta font-bold text-foreground mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              value={formData.volunteerPhone}
              onChange={(e) => setFormData({ ...formData, volunteerPhone: e.target.value })}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground font-body"
              placeholder="+1 (555) 123-4567"
              disabled={isSubmitting}
            />
            {errors.volunteerPhone && (
              <p className="text-error text-sm mt-1 font-body">{errors.volunteerPhone}</p>
            )}
          </div>

          {/* Skills */}
          <div>
            <label className="block text-sm font-cta font-bold text-foreground mb-2">
              Your Skills *
            </label>
            <div className="flex space-x-2 mb-3">
              <input
                type="text"
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSkill())}
                className="flex-1 px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground font-body"
                placeholder="Add a skill (e.g., First Aid, Teaching)"
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={handleAddSkill}
                disabled={isSubmitting}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-cta font-bold hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.skills.map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-body flex items-center space-x-2"
                >
                  <span>{skill}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveSkill(skill)}
                    disabled={isSubmitting}
                    className="hover:text-primary/70 disabled:opacity-50"
                  >
                    <Icon name="XMarkIcon" size={14} variant="outline" />
                  </button>
                </span>
              ))}
            </div>
            {errors.skills && (
              <p className="text-error text-sm mt-1 font-body">{errors.skills}</p>
            )}
          </div>

          {/* Availability */}
          <div>
            <label className="block text-sm font-cta font-bold text-foreground mb-2">
              Availability *
            </label>
            <select
              value={formData.availability}
              onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground font-body"
              disabled={isSubmitting}
            >
              <option value="">Select your availability</option>
              <option value="Immediately">Immediately</option>
              <option value="Within 1 month">Within 1 month</option>
              <option value="Within 3 months">Within 3 months</option>
              <option value="Within 6 months">Within 6 months</option>
              <option value="Flexible">Flexible</option>
            </select>
            {errors.availability && (
              <p className="text-error text-sm mt-1 font-body">{errors.availability}</p>
            )}
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-cta font-bold text-foreground mb-2">
              Why do you want to volunteer? *
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={4}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground font-body resize-none"
              placeholder="Tell us about your motivation and what you hope to contribute..."
              disabled={isSubmitting}
            />
            {errors.message && (
              <p className="text-error text-sm mt-1 font-body">{errors.message}</p>
            )}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="p-4 bg-error/10 border border-error rounded-lg">
              <p className="text-error text-sm font-body">{errors.submit}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-4 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-muted text-foreground rounded-lg font-cta font-bold hover:bg-muted/80 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-cta font-bold hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" variant="outline" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Icon name="PaperAirplaneIcon" size={20} variant="solid" />
                  <span>Submit Application</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VolunteerApplicationModal;